import React, { useContext, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
} from "@mui/material";
import { CircularProgress } from "@material-ui/core";
import { Typography } from "@material-ui/core";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { findUserNameById } from "@thequantumhub/iam-package";
import { useSnackbar } from "notistack";
import { viewportContext } from "../../../context/ViewportContext";
import { updateRecordsNarcotics } from "../../../service/endpoint-manager";

import NarcoticsQualitativeQuestions from "./NarcoticsQualitativeQuestions";
import { narcoticsPDFReport } from "./NarcoticsPDFReport";

import styles from "../../../styles.module.css";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";

const formatCreatedAtDate = (createdAt) => {
  const date = new Date(createdAt);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#415970",
    color: "#ffffff",
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
}));

const LoadingSpinner = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        // height: "30px",
        // width: "30vh",
      }}
    >
      <CircularProgress size={25} />
    </div>
  );
};

const NarcoticsReportSummary = ({
  narcoticsRecords,
  setNarcoticsRecords,
  filteredNarcoticsRecords,
  setFilteredNarcoticsRecords,
  userNames,
  dataFetched,
  isDashboard,
  setIsDashboard,
}) => {
  const { screenType } = useContext(viewportContext);
  const { enqueueSnackbar } = useSnackbar();

  const [deletingRecordId, setDeletingRecordId] = useState(null);

  if (!filteredNarcoticsRecords) return <div>Loading...</div>;

  // to delete records - uses update api
  const handleDelete = async (recordId) => {
    setDeletingRecordId(recordId);

    try {
      // Prepare the data to mark the record as deleted
      const qualitativeData = { deleted: true };

      const response = await updateRecordsNarcotics(recordId, qualitativeData);

      if (response.qualitative) {
        const updatedRecords = narcoticsRecords.filter(
          (record) => record.id !== recordId
        );
        setNarcoticsRecords(updatedRecords);
        enqueueSnackbar("Narcotics Record Deleted Successfully", {
          variant: "success",
        });
        console.log("Narcotics Record Deleted Successfully!");
        setDeletingRecordId(null);
      } else {
        console.error("Failed to delete the record", response);
        setDeletingRecordId(null);
        enqueueSnackbar("Failed to delete record", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(
        "Error deleting the record:",
        error.response ? error.response.data : error
      );
      enqueueSnackbar("Failed to delete record", {
        variant: "error",
      });
      setDeletingRecordId(null);
    }
  };

  // to restore deleted records - uses update api
  const handleRestore = async (recordId) => {
    setDeletingRecordId(recordId);

    try {
      // Prepare the data to mark the record as deleted
      const qualitativeData = { deleted: false };

      const response = await updateRecordsNarcotics(recordId, qualitativeData);

      if (response.qualitative) {
        const updatedRecords = narcoticsRecords.map((record) =>
          record.id === recordId ? { ...record, deleted: false } : record
        );
        setNarcoticsRecords(updatedRecords);
        enqueueSnackbar("Narcotics Record Restored Successfully", {
          variant: "success",
        });
        console.log("Narcotics Record Restored Successfully!");
        setDeletingRecordId(null);
      } else {
        console.error("Failed to restore the record", response);
        setDeletingRecordId(null);
        enqueueSnackbar("Failed to restore record", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(
        "Error restoring the record:",
        error.response ? error.response.data : error
      );
      enqueueSnackbar("Failed to restore the record", {
        variant: "error",
      });
      setDeletingRecordId(null);
    }
  };

  return (
    <Paper className={styles.paperStyles}>
      <div>
        <div className={styles.titleButtonRow}>
          <Typography variant="h5">Audit Report Summary</Typography>
          <div className={styles.exportButtonContainer}>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={clsx(
                  styles.primaryButton,
                  styles.complianceTableButtons
                )}
                onClick={() => setIsDashboard(!isDashboard)}
              >
                + New Observation
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={clsx(
                  styles.secondaryButton,
                  styles.complianceTableButtons
                )}
                onClick={() =>
                  narcoticsPDFReport(filteredNarcoticsRecords, userNames)
                }
              >
                {screenType.isMobile ? "PDF" : "Export PDF"}
              </Button>
            </div>
          </div>
        </div>
        <TableContainer className={styles.narcoticsReportTableContainer}>
          {dataFetched ? (
            <Table stickyHeader aria-label="narcotics records">
              <TableHead>
                <TableRow className={styles.narcoticsReportHeader}>
                  <TableCell>Date</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Completed By</TableCell>
                  <TableCell>Facility Designate Name</TableCell>
                  <TableCell>Pharmacy Designate Name</TableCell>
                  <TableCell>Total Records</TableCell>
                  <TableCell>Passed Records</TableCell>
                  <TableCell>Failed Records</TableCell>
                  {NarcoticsQualitativeQuestions.map((question, index) => (
                    <CustomTooltip
                      title={question}
                      enterTouchDelay={0}
                      placement="top"
                      arrow
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [0, -40],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <TableCell style={{ cursor: "help" }} key={index}>{`Q${
                        index + 1
                      }`}</TableCell>
                    </CustomTooltip>
                  ))}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredNarcoticsRecords.map((record, recordIndex) => (
                  <TableRow key={recordIndex}>
                    <TableCell>
                      {formatCreatedAtDate(record.createdAt)}
                    </TableCell>
                    <TableCell>{record.facility}</TableCell>
                    <TableCell>{record.unit}</TableCell>
                    <TableCell>
                      {findUserNameById(
                        record.createdBy,
                        userNames,
                        "user_id",
                        "userName"
                      )}
                    </TableCell>
                    <TableCell>{record.facilityDesignateName}</TableCell>
                    <TableCell>{record.pharmacyDesignateName}</TableCell>
                    <TableCell>
                      {record?.quantitativeCompliance?.totalRecords}
                    </TableCell>
                    <TableCell>
                      {record.quantitativeCompliance.passedRecords}
                    </TableCell>
                    <TableCell>
                      {record.quantitativeCompliance?.totalRecords -
                        record.quantitativeCompliance.passedRecords}
                    </TableCell>
                    {NarcoticsQualitativeQuestions.map((question, index) => (
                      <TableCell key={index}>
                        {record.qualitative[`q${index + 1}`].answer}
                      </TableCell>
                    ))}
                    <TableCell>
                      {deletingRecordId === record.id ? (
                        <LoadingSpinner />
                      ) : record.deleted ? (
                        <IconButton
                          onClick={() => handleRestore(record.id)}
                          aria-label="restore"
                        >
                          <ReplayIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => handleDelete(record.id)}
                          aria-label="delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p style={{ textAlign: "center", marginTop: "30px" }}>
              Please Wait While The Data is Being Fetched ...
            </p>
          )}
        </TableContainer>
      </div>
    </Paper>
  );
};

export default NarcoticsReportSummary;
